import type { ReactNode } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, EyeOff } from "lucide-react";

import { cn } from "../../lib/utils.js";
import { Button } from "../ui/button.js";
import { SelectField } from "../ui/select-field.js";
import {
  WIDGET_SIZE_CLASS, WIDGET_SIZE_LABELS, sizesFor,
  type DashboardWidgetDef, type WidgetSize,
} from "./widget-registry.js";

/**
 * The chrome around one widget: grid span, drag handle, size picker and hide
 * button. It is the only place that knows a widget can be moved or resized —
 * the widget's own component receives none of that (interface segregation).
 */
export interface WidgetFrameProps {
  def: DashboardWidgetDef;
  size: WidgetSize;
  editing: boolean;
  onSizeChange: (size: WidgetSize) => void;
  onHide: () => void;
  children: ReactNode;
}

export function WidgetFrame({ def, size, editing, onSizeChange, onHide, children }: WidgetFrameProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: def.key, disabled: !editing });

  return (
    <div
      ref={setNodeRef}
      // dnd-kit computes a transform per frame from live pointer position, which
      // no static class can express (CLAUDE.md JS-driven animation exception).
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "col-span-1 min-w-0",
        WIDGET_SIZE_CLASS[size],
        isDragging && "z-10 opacity-60",
      )}
    >
      {editing ? (
        <div className="relative rounded-[var(--radius-xl)] border-2 border-dashed border-brand-300 p-2">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="flex cursor-grab items-center gap-1 rounded-[var(--radius-md)] px-1.5 py-1 text-[11px] font-semibold text-surface-700 hover:bg-surface-100 active:cursor-grabbing"
              aria-label={`Move ${def.title}`}
              {...attributes}
              {...listeners}
            >
              <GripVertical size={14} />
              {def.title}
            </button>

            <SelectField
              value={size}
              onValueChange={(v) => onSizeChange(v as WidgetSize)}
              size="sm"
              className="ml-auto w-32"
              aria-label={`Width of ${def.title}`}
            >
              {sizesFor(def).map((s) => (
                <option key={s} value={s}>{WIDGET_SIZE_LABELS[s]}</option>
              ))}
            </SelectField>

            {/* A required widget can be moved and resized but not removed. */}
            {!def.required && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onHide}
                title={`Hide ${def.title}`}
                aria-label={`Hide ${def.title}`}
              >
                <EyeOff size={14} />
              </Button>
            )}
          </div>

          {/* Inert while arranging: clicks move the card, they don't follow links. */}
          <div className="pointer-events-none select-none">{children}</div>
        </div>
      ) : (
        children
      )}
    </div>
  );
}
